import Loader from '@/Utility/Loader';
import RADApplication from '@/Management/RADApplication';
import ComponentManager from '@/Management/ComponentManager';
import * as LoggingManager from '@/Utility/LoggingManager';
import ResourceManager from '@/Management/ResourceManager';
import MessageBoxViewModelBuilder from '@/Builder/MessageBoxViewModelBuilder';
import NavigationService from '@/Management/NavigationService';
import Utilities from '@/Utility/Utilities';

type ErrorHandler = (msg: string, script: string, line: string | number, column: string | number, err: Error) => boolean;

export default class ExceptionBuilder {

    public static hasCommunicationError: boolean = false;
    private static instance: ExceptionBuilder;

    private errorHandlers: ErrorHandler[] = [];

    public static init() {
        window.onerror = ExceptionBuilder.handleError;
    }

    private constructor() { }

    private static getInstance() {
        if (ExceptionBuilder.instance == null)
            ExceptionBuilder.instance = new ExceptionBuilder();

        return ExceptionBuilder.instance;
    }

    private static handleError(msg: string, script: string, line: string | number, column: string | number, err: Error) {
        let handled = false;
        for (const handler of ExceptionBuilder.getInstance().errorHandlers) {
            if (handler(msg, script, line, column, err))
                handled = true;
        }
        if (handled)
            return;

        ExceptionBuilder.throw(msg, script, line, column, err);
    }

    public static registerErrorHandler(handler: ErrorHandler) {
        ExceptionBuilder.getInstance().errorHandlers.push(handler);
    }

    public static throw(msg: string, script: string, line: string | number, column: string | number, err: Error) {
        if (script) {
            script = script.replace(MODEL.HTMLClientResourceURL, '');

            let extensionIndex = script.indexOf('Extension/');
            if (extensionIndex > -1) {
                script = script.substring(extensionIndex + 10);
            }
        } else {
            script = 'missing';
        }

        let stack = ExceptionBuilder.getInstance().getStackFromError(err);
        let message = msg + '\nScript: ' + script + '\nZeile: ' + line + ' Spalte: ' + column;

        ExceptionBuilder.getInstance().showError(message, stack, null);
        Loader.getInstance().removeAll();
    }

    public static onCommunicationError() {
        if (ExceptionBuilder.hasCommunicationError === false) {
            ExceptionBuilder.hasCommunicationError = true;

            ExceptionBuilder.getInstance().showCommunicationError();
            Loader.getInstance().removeAll();
        }
    }

    public static throwDotNet(operation: string, keys, responseJSON: any, responseText: string, args) {
        let data = {};

        for (let i = 0; i < keys.length; i++) {
            data[keys[i]] = args[keys[i]] || null;
        }

        const message = ResourceManager.getInstance().get('OPERATION_CALL_FAILED')
            .replace('$$operation$$', operation);

        const builder = ExceptionBuilder.getInstance();
        const anonymizedData = builder.anonymizeData(data);

        if (responseJSON) {
            const stack = builder.getStackFromException(responseText);
            const messageBox = new MessageBoxViewModelBuilder()
                .initForExceptionInfoMessageBox(responseJSON.ErrorInformation, message, stack, anonymizedData)
                .build();
            RADApplication.getInstance().showMessageBox(messageBox);
        } else if (responseText) {
            const stack = builder.getStackFromException(responseText);
            builder.showError(message, stack, anonymizedData, true);
        }

        Loader.getInstance().removeAll();
    }

    private showCommunicationError() {
        let MessageBoxViewModel = ComponentManager.getViewModel('MessageBoxViewModel') as any;

        if (MessageBoxViewModel != null) {
            RADApplication.getInstance().showMessageBox(MessageBoxViewModel.createErrorMessageBox(null, 'COMMUNICATION_ERROR', null, null, () => {
                NavigationService.getInstance().reload();
            }));
        }
    }

    private showError(message: string, stack: string, data: any, withoutErgNames?: boolean): void {
        const MessageBoxViewModel = ComponentManager.getViewModel('MessageBoxViewModel') as any;

        if (MessageBoxViewModel != null) {
            if (withoutErgNames) {
                RADApplication.getInstance().showMessageBox(MessageBoxViewModel.createErrorMessageBoxWithoutErgnames(message, stack, data));
            }
            else {
                RADApplication.getInstance().showMessageBox(MessageBoxViewModel.createErrorMessageBox(message, null, stack, data));
            }

            LoggingManager.logError(LoggingManager.LogLevel.Error, document.location.href, message + ' \n\r ' + stack);
        }
    }

    private getStackFromError(err) {
        let stack = err.stack.split('\n');
        let clientStack = [];

        for (let line of stack) {
            let rest = 221 - line.length;
            let blank = '\u00A0';
            let replacement = '';

            for (let j = 1; j <= rest; j++) {
                replacement += blank;
            }

            line = line.replace('@', replacement);

            if (line.indexOf('Scripts/') === -1) {
                clientStack.push(line);
            }
        }

        return clientStack.join('\n');
    }

    private getStackFromException(exc) {
        let stack = exc + '';

        stack = stack.replace(/\"/g, '');
        stack = stack.replace(/\s/g, '\u00A0');
        stack = stack.replace(/\\u003c/g, '<');
        stack = stack.replace(/\\u003e/g, '>');
        stack = stack.replace(/([^>\\r\\n]?)(\\r\\n|\\n\\r|\\r|\\n)/g, '$1\n');

        return stack;
    }

    /**
     * Anonymisiert Fehlermeldungen
     * @param data
     */
    private anonymizeData(data): object {
        if (Utilities.isNullOrUndefinedOrBlank(data))
            return data;

        if (!Utilities.isNullOrUndefinedOrBlank(data.values)) {
            data.values = this.anonymizeValues(data.values);
        }
        if (!Utilities.isNullOrUndefinedOrBlank(data.valuesToUpdate)) {
            data.valuesToUpdate = this.anonymizeValues(data.valuesToUpdate);
        }
        if (!Utilities.isNullOrUndefinedOrBlank(data.valuesToValidate)) {
            data.valuesToValidate = this.anonymizeValues(data.valuesToValidate);
        }

        return data;
    }

    /**
     * Hilfsmethode zum Anonymisieren von JSON-Strings
     * @param dataString
     */
    private anonymizeValues(dataString: string): string {
        if (dataString.indexOf('Password') === -1) {
            return dataString;
        }
        
        try {
            const valuesObject = JSON.parse(dataString);
            _.keys(valuesObject).forEach((key) => {
                if (key.indexOf('Password') > -1)
                    valuesObject[key] = '';
            });

            return JSON.stringify(valuesObject);
        } catch (e) {
            return dataString;
        }
    }
}